import { DOW_LABELS, addDays, parseDay } from "./date";
import {
  MICRO_KEYS,
  OIL_FAT_G_PER_TSP,
  OIL_KCAL_PER_TSP,
  PARTIAL_KEYS,
  type PartialKey,
  lacking,
  loggedIdFor,
  planFor,
  sumState,
  totalsOf,
} from "./intake";
import type { WorkParts } from "./today";
import type { FoodEntry, FoodItem, KayaState } from "./types";

/* The Food tab's words and small arithmetic, kept out of the pages so they can be tested. */

/** "Thu 24" — a day row inside one week, where the month is never in doubt. */
export function dayShort(ds: string): string {
  const d = parseDay(ds);
  return `${DOW_LABELS[d.getDay()]} ${d.getDate()}`;
}

const r = Math.round;

/** "P 22 · C 3 · F 26" */
export function macroText(e: Pick<FoodEntry, "p" | "c" | "f">): string {
  return `P ${r(e.p)} · C ${r(e.c)} · F ${r(e.f)}`;
}

/** "3 katori · 1 tsp oil" — how much, in the unit it was logged in. */
export function portionText(e: Pick<FoodEntry, "qty" | "unit" | "oilTsp">): string {
  return `${e.qty.toLocaleString()} ${e.unit}${e.oilTsp ? ` · ${e.oilTsp} tsp oil` : ""}`;
}

/* ---------- the "~" mark (SNIPPETS §39) ----------
   An INDB dish is the table's usual recipe; a home version differs mostly by the oil. Its kcal
   carries "~" wherever a row shows it — never a total, which is a sum and would always read "~".
   Derived from where the food came from at render time, not stored on the entry: the entry already
   carries the row's code, and INDB's codes are its three recipe books. A staple, a label food, a
   food of the person's own and FNDDS's priced-as-eaten dishes carry none. */

/** INDB's recipe books — ASC, BFP, OSR — the only codes a `src: "i"` row has. */
const INDB_CODE = /^(ASC|BFP|OSR)\d/;

/** A table row whose figure is an estimate of a home dish. */
export function isEstimatedFood(f: Pick<FoodItem, "src">): boolean {
  return f.src === "i";
}

/** A logged or planned entry copied from such a row. */
export function isEstimatedEntry(e: Pick<FoodEntry, "code">): boolean {
  return !!e.code && INDB_CODE.test(e.code);
}

/** "~328 kcal" for an estimated dish, "558 kcal" otherwise. */
export function kcalText(kcal: number, estimated: boolean): string {
  return `${estimated ? "~" : ""}${Math.round(kcal).toLocaleString()} kcal`;
}

/** What a screen reader hears instead of the "~": "about 328 kcal, estimated". */
export function kcalSpoken(kcal: number, estimated: boolean): string {
  const n = `${Math.round(kcal).toLocaleString()} kcal`;
  return estimated ? `about ${n}, estimated` : n;
}

/** The one legend line a screen showing a "~" carries, after the "~" itself. */
export const ESTIMATE_LEGEND = "an estimated dish — the table’s usual recipe; home versions vary";

/** The same, in an estimated dish's own entry sheet. */
export const ESTIMATE_ENTRY = "Estimated — home versions vary";

/* ---------- partial sums (SNIPPETS §31, ADR-037) ----------
   A day's sodium over six foods, one of which has no sodium on record, is not "1,900 mg" — it is
   at least 1,900. The panel marks such a sum "≥", names the foods under the bar, and says once at
   the end what "≥" means. An absent value is "not known", never 0 (invariant 18). */

export const NUTRIENT_WORD: Readonly<Record<PartialKey, string>> = {
  fb: "fibre",
  sf: "saturated fat",
  na: "sodium",
  ca: "calcium",
  fe: "iron",
  pot: "potassium",
};

/** A food's name short enough for a line under a bar: INDB's bracketed local names dropped. */
export function shortFoodName(name: string): string {
  const s = name.replace(/\s*\(.*$/, "").trim();
  return s || name;
}

const own = (e: FoodEntry) => (e.code?.startsWith("cf:") ? " (yours)" : "");

/** "a", "a or b", "a, b or c" */
function orList(words: string[]): string {
  return words.length <= 1 ? (words[0] ?? "") : `${words.slice(0, -1).join(", ")} or ${words[words.length - 1]}`;
}

/**
 * The words under a partial bar — "Mango pickle not known", "Chai and Roti not known",
 * "3 foods not known" — or null when the sum is complete or there is none to mark.
 */
export function partialMark(entries: FoodEntry[], key: PartialKey): string | null {
  const t = totalsOf(entries);
  if (sumState(t, key) !== "partial") return null;
  const names = [...new Set(lacking(entries, key).map((e) => shortFoodName(e.name)))];
  if (names.length === 1) return `${names[0]} not known`;
  if (names.length === 2) return `${names[0]} and ${names[1]} not known`;
  return `${names.length} foods not known`;
}

const COUNT_WORD = ["", "that one", "those two", "those three", "those four", "those five", "those six"];

/**
 * The one line at the end of the nutrient panel when any sum is short, or null when every food
 * carries every value. It names the foods and what each lacks, because "2 of 7 items" sends you
 * hunting, and it says what the "≥" on a bar means.
 */
export function partialFootnote(entries: FoodEntry[]): string | null {
  const t = totalsOf(entries);
  const byName = new Map<string, Set<PartialKey>>();
  for (const e of entries) {
    const missing = PARTIAL_KEYS.filter((k) => typeof e[k] !== "number");
    if (!missing.length) continue;
    const name = `${e.name}${own(e)}`;
    const cur = byName.get(name) ?? new Set<PartialKey>();
    for (const k of missing) cur.add(k);
    byName.set(name, cur);
  }
  if (!byName.size) return null;

  const words = (keys: Iterable<PartialKey>) =>
    orList(PARTIAL_KEYS.filter((k) => [...keys].includes(k)).map((k) => NUTRIENT_WORD[k]));
  const what =
    byName.size <= 3
      ? [...byName].map(([name, keys]) => `${name} has no ${words(keys)}`).join("; ")
      : `${byName.size} foods have no ${words(new Set([...byName.values()].flatMap((s) => [...s])))}`;

  const partial = PARTIAL_KEYS.filter((k) => sumState(t, k) === "partial").length;
  if (!partial) return `${what} on record.`;
  const those = COUNT_WORD[partial] ?? `those ${partial}`;
  return `≥ means a food lacks that value: ${what} on record, so ${those} ${partial === 1 ? "is" : "are"} at least what is shown.`;
}

/** "182 session + 96 treadmill + 41 walk", or null when no work was logged (invariant 11). */
export function workLine(w: WorkParts): string | null {
  const parts: string[] = [];
  if (w.session !== null) parts.push(`${w.session.toLocaleString()} session`);
  if (w.treadmill !== null) parts.push(`${w.treadmill.toLocaleString()} treadmill`);
  if (w.walk !== null) parts.push(`${w.walk.toLocaleString()} walk`);
  return parts.length ? parts.join(" + ") : null;
}

/** How many days from `today` on have anything planned — the Plan the week row's value. */
export function plannedDayCount(S: KayaState, today: string, span = 7): number {
  let n = 0;
  for (let i = 0; i < span; i++) if (planFor(S, addDays(today, i)).length) n++;
  return n;
}

export interface PlanWeek {
  days: { ds: string; kcal: number | null; protein: number | null; items: number }[];
  /** the planned days' average; null when none is planned */
  avgKcal: number | null;
  avgProtein: number | null;
  planned: number;
}

/** The next seven days of the plan, and what the planned ones average. Unplanned days are left
 *  out of the average rather than counted as zero-calorie days. */
export function planWeek(S: KayaState, today: string): PlanWeek {
  const days = Array.from({ length: 7 }, (_, i) => {
    const ds = addDays(today, i);
    const items = planFor(S, ds);
    const t = totalsOf(items);
    return {
      ds,
      kcal: items.length ? t.kcal : null,
      protein: items.length ? r(t.p) : null,
      items: items.length,
    };
  });
  const withPlan = days.filter((d) => d.items);
  const avg = (f: (d: (typeof days)[number]) => number) =>
    withPlan.length ? r(withPlan.reduce((a, d) => a + f(d), 0) / withPlan.length) : null;
  return {
    days,
    avgKcal: avg((d) => d.kcal ?? 0),
    avgProtein: avg((d) => d.protein ?? 0),
    planned: withPlan.length,
  };
}

/**
 * The `p:<item>` stem an entry was logged from the plan under, or null. The longest match wins,
 * because a re-applied template item's own id can carry a `:2` suffix of its own.
 */
export function planStem(S: KayaState, ds: string, entryId: string): string | null {
  let best: string | null = null;
  for (const item of planFor(S, ds)) {
    const base = loggedIdFor(item.id);
    if ((entryId === base || entryId.startsWith(`${base}:`)) && (!best || base.length > best.length))
      best = base;
  }
  return best;
}

/**
 * The id an edited entry is written under. An edit is a tombstone plus a new id (ADR-026), and a
 * tombstoned id can never live again, so `taken` holds the day's live ids and its tombstones. An
 * entry logged from the plan keeps its `stem` (see `planStem`) so the plan still counts it as
 * eaten, taking the next free suffix.
 */
export function editedEntryId(stem: string | null, taken: Set<string>, now: number): string {
  if (stem) {
    let n = 2;
    while (taken.has(`${stem}:${n}`)) n++;
    return `${stem}:${n}`;
  }
  let id = `${now.toString(36)}-e`;
  let n = 2;
  while (taken.has(id)) id = `${now.toString(36)}-e${n++}`;
  return id;
}

const round1 = (n: number) => Math.round(n * 10) / 10;

/**
 * The same entry at a new portion and a new amount of oil, from its own frozen values.
 *
 * Deliberately not re-read from the food table: values are copied at log time so a regenerated
 * table never changes a past meal (ADR-025), and an edit must not do it either. The oil is taken
 * off, the food part scaled by grams, the oil put back. Every nutrient the entry carries is carried
 * over; one it lacks stays absent, never 0 (invariant 18). Null when the entry has no grams to
 * scale from, or the new portion is empty.
 */
export function reportion(
  e: FoodEntry,
  opts: { qty: number; oilTsp: number; id: string; at?: number }
): FoodEntry | null {
  if (!(e.grams > 0) || !(e.qty > 0) || !Number.isFinite(opts.qty) || opts.qty <= 0) return null;
  const s = opts.qty / e.qty;
  const oldOil = e.oilTsp || 0;
  const oil = Math.max(0, opts.oilTsp);
  const foodKcal = Math.max(0, e.kcal - oldOil * OIL_KCAL_PER_TSP);
  const foodFat = Math.max(0, e.f - oldOil * OIL_FAT_G_PER_TSP);
  const out: FoodEntry = {
    ...e,
    id: opts.id,
    at: opts.at ?? e.at,
    qty: opts.qty,
    grams: Math.round(e.grams * s),
    kcal: Math.round(foodKcal * s + oil * OIL_KCAL_PER_TSP),
    p: round1(e.p * s),
    c: round1(e.c * s),
    f: round1(foodFat * s + oil * OIL_FAT_G_PER_TSP),
  };
  if (typeof e.fb === "number") out.fb = round1(e.fb * s);
  if (oil > 0) out.oilTsp = oil;
  else delete out.oilTsp;
  for (const k of MICRO_KEYS) {
    const v = e[k];
    if (typeof v === "number") out[k] = k === "sf" || k === "fe" ? round1(v * s) : Math.round(v * s);
    else delete out[k];
  }
  return out;
}
